import { AgeVariant, getAgeVariant } from "utils/age";
import { QuestionType, QuestionUsageType } from "consts";
import { isNullOrUndefOrEmpty, toObject } from "utils";

import AgeNumpadQuestion from "components/questions/AgeNumpadQuestion";
import AgePickerQuestion from "components/questions/AgePickerQuestion";
import AgeSliderQuestion from "components/questions/AgeSliderQuestion";
import CheckboxQuestion from "components/questions/CheckboxQuestion";
import DecorativeImage from "components/decorative-image/DecorativeImage";
import MatrixQuestion from "components/questions/MatrixQuestion";
import PollBy from "components/poll-by/PollBy";
import PropTypes from "prop-types";
import RadioQuestion from "components/questions/RadioQuestion";
import React from "react";
import SpectrumQuestion from "components/questions/SpectrumQuestion";
import TextQuestion from "components/questions/TextQuestion";
import classNames from "classnames";
import i18n from "micro-i18n";

//FIXME: SOF-617 requires the following imports to be moved to components/widget-components.scss
// due to lazy loading of the widget inside an iframe. Keeping this here for potential fix down the road.
// import 'components/questions/Question.scss';

/**
 *
 * @param {Object} question
 * @param {{[showResultsButton]:boolean,[showPreviousButton]:boolean,[submitEnabled]:boolean,
 * [isDisabled]:boolean}} questionState
 * @param {React.Ref} topFocusRef
 * @param {function} onPreviousQuestion
 * @param {function} onSubmit
 * @param {string} [complianceUrl]
 * @param {string} [partnerName]
 * @param {boolean} [isSubmitting]
 * @param {boolean} [hasNextButtonCompliance]
 * @param {boolean} [focusable]
 * @param {boolean} [expandable]
 * @param {boolean} [pollByEnabled]
 * @param {boolean} [horizontalOptionsEnabled]
 * @param {boolean} [testNoAutoAdvanceEnabled]
 * @param {boolean} [testQuestionDynamicNextEnabled]
 * @param {string[]} [natures]
 * @return {JSX.Element|null}
 * @constructor
 */
const Question = ({
  question,
  questionState = {},
  topFocusRef,
  onPreviousQuestion,
  onSubmit,
  complianceUrl,
  partnerName,
  isSubmitting = false,
  hasNextButtonCompliance = false,
  focusable = false,
  expandable = false,
  pollByEnabled = false,
  horizontalOptionsEnabled = false,
  testNoAutoAdvanceEnabled = false,
  testQuestionDynamicNextEnabled = false,
  natures = [],
}) => {
  //Return nothing when there are no question
  if (!question) {
    return null;
  }
  const { id: questionId, text, type, usage, img, imgAlt } = toObject(question);
  const { submitEnabled, showResultsButton, showPreviousButton, isDisabled = true } = questionState;
  const questionLabelId = `csw-q-${questionId}`;

  const ageVariant = getAgeVariant(questionId, natures);

  const hasComplianceUrl = !isNullOrUndefOrEmpty(complianceUrl);
  const showComplianceMessaging = hasComplianceUrl && hasNextButtonCompliance;
  const isPinned = usage === QuestionUsageType.Pinned;

  const handleSubmit = () => {
    !isSubmitting && onSubmit();
  };

  const onFormSubmit = (e) => e.preventDefault();

  const showButtons = (expandable && focusable) || !expandable;
  const showNextButton =
    (!!ageVariant && ageVariant !== AgeVariant.Picker) ||
    type !== QuestionType.Radio ||
    showComplianceMessaging ||
    !testQuestionDynamicNextEnabled;

  return (
    <form
      role="form"
      autoComplete="off"
      className={classNames("csw-question", {
        "csw-q-wait": isSubmitting,
      })}
      aria-disabled={isDisabled}
      onSubmit={onFormSubmit}
    >
      <div className="csw-question-wrapper csw-scroll-section">
        <div
          className={classNames("csw-question-text csw-title-text outline")}
          id={questionLabelId}
          role="heading"
          aria-level="1"
          title={text}
          ref={isDisabled ? null : topFocusRef}
          tabIndex="0"
        >
          {img && <DecorativeImage className="csw-question-text-image" src={img} alt={imgAlt || ""} />}
          <span>{text}</span>
        </div>
        {question && ageVariant === AgeVariant.Slider && (
          <AgeSliderQuestion labelId={questionLabelId} question={question} isDisabled={isDisabled} />
        )}

        {question && ageVariant === AgeVariant.Picker && (
          <AgePickerQuestion labelId={questionLabelId} question={question} isDisabled={isDisabled} />
        )}

        {question && ageVariant === AgeVariant.Numpad && (
          <AgeNumpadQuestion labelId={questionLabelId} question={question} isDisabled={isDisabled} />
        )}

        {question && !ageVariant && type === QuestionType.Radio && (
          <RadioQuestion
            labelId={questionLabelId}
            question={question}
            isDisabled={isDisabled}
            horizontalOptionsEnabled={horizontalOptionsEnabled}
            testNoAutoAdvanceEnabled={testNoAutoAdvanceEnabled}
          />
        )}

        {question && type === QuestionType.Checkbox && (
          <CheckboxQuestion
            question={question}
            isDisabled={isDisabled}
            horizontalOptionsEnabled={horizontalOptionsEnabled}
          />
        )}

        {question && type === QuestionType.Matrix && <MatrixQuestion question={question} isDisabled={isDisabled} />}

        {question && type === QuestionType.Spectrum && <SpectrumQuestion question={question} isDisabled={isDisabled} />}

        {question && type === QuestionType.Text && <TextQuestion question={question} isDisabled={isDisabled} />}
      </div>

      <div className="csw-scroll-shadow" />

      {showButtons && (
        <>
          {showComplianceMessaging && (
            <div className="csw-compliance-first-page-text">
              {"\u002A "}
              {i18n.t("COMPLIANCE_FIRST_PAGE_MESSAGE")}
              <a className="csw-compliance-link outline" href={complianceUrl} target="_blank" rel="noopener noreferrer">
                {i18n.t("COMPLIANCE_FIRST_PAGE_LINK")}
              </a>
            </div>
          )}

          <div className="csw-question-btns csw-btns csw-stretch">
            <button
              type="button"
              role="button"
              disabled={!submitEnabled || isDisabled}
              className={classNames("csw-btn csw-btn-primary csw-btn-submit outline", {
                "csw-btn-wait": isSubmitting || isDisabled,
                "sr-only": !showNextButton,
              })}
              tabIndex="0"
              onClick={handleSubmit}
            >
              {i18n.t(showResultsButton ? "BUTTON_SEE_RESULTS" : "BUTTON_NEXT")}
              {showComplianceMessaging && " \u002A"}
            </button>

            {showPreviousButton && (
              <button
                type="button"
                className="csw-btn csw-btn-first outline"
                disabled={isDisabled}
                tabIndex="0"
                onClick={onPreviousQuestion}
              >
                {i18n.t("BUTTON_BACK")}
              </button>
            )}
          </div>

          {pollByEnabled && <PollBy id="question-poll-by" partnerName={partnerName} hasTooltip={isPinned} />}
        </>
      )}
    </form>
  );
};

Question.propTypes /* remove-proptypes */ = {
  question: PropTypes.object.isRequired,
  questionState: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPreviousQuestion: PropTypes.func,
  topFocusRef: PropTypes.object.isRequired,
  isSubmitting: PropTypes.bool,
  complianceUrl: PropTypes.string,
  partnerName: PropTypes.string,
  hasNextButtonCompliance: PropTypes.bool,
  focusable: PropTypes.bool,
  expandable: PropTypes.bool,
  pollByEnabled: PropTypes.bool,
  horizontalOptionsEnabled: PropTypes.bool,
  testExpandableOnScrollEnabled: PropTypes.bool,
  testNoAutoAdvanceEnabled: PropTypes.bool,
  testQuestionDynamicNextEnabled: PropTypes.bool,
  natures: PropTypes.arrayOf(PropTypes.string),
};

export default Question;
